import type { ReactNode } from 'react';

export interface FormField {
    value: string;
    error: string;
}

/**
 * `value` is undefined when nothing is chosen: either the column is null (`cages.cagetypeid` and
 * `cages.fishtypeid` both are) or the user picked the select's placeholder option. Both ids are
 * optional on the PATCH, so an unset value is simply left out of the request and the cage keeps
 * what it has — the other fields stay editable either way.
 */
export interface OptionalIdFormField {
    value: number | undefined;
    error: string;
}

export interface FormState {
    name: FormField;
    cageType: OptionalIdFormField;
    fishType: OptionalIdFormField;
    externalName: FormField;
    externalId: FormField;
    id: number;
}

export interface FormStatus {
    isSubmitting: boolean;
    /** What the last save failed with, as copy or as `ApiErrorMessage`. */
    error: ReactNode;
    successMessage: string;
}

/**
 * Named primitives rather than the context blocks: a form that needs five values should say so,
 * not take the whole cage context and reach into it.
 */
export interface SettingsFormProps {
    cageId: number;
    cageName: string;
    /** Undefined when the cage has no type set; the save then leaves the type untouched. */
    cageTypeId?: number;
    /** The pen's species. Undefined when neither it nor its location has one. */
    fishTypeId?: number;
    externalId: string;
    externalName: string;
    locationTypeShortName?: string;
}

export const makeInitialFormState = (props: SettingsFormProps): FormState => ({
    name: { value: props.cageName, error: '' },
    cageType: { value: props.cageTypeId, error: '' },
    // The pen's own species. Correcting a mislabelled pen used to need a database write, which
    // is what forced the TROUT_CAGE_ID_OVERRIDE list in batch-service-bigquery (WB-2207).
    fishType: { value: props.fishTypeId, error: '' },
    externalName: { value: props.externalName, error: '' },
    externalId: { value: props.externalId, error: '' },
    id: props.cageId,
});

export const INITIAL_STATUS: FormStatus = {
    isSubmitting: false,
    error: '',
    successMessage: '',
};
